import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withLDConsumer, useFlags } from 'launchdarkly-react-client-sdk';
import { Navbar } from '@agconnections/grow-ui';

import brandIcon from 'assets/cropwise-financials-icon.svg';
import brandText from 'assets/cropwise-financials-text.svg';
import { Context } from 'components/Store';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import { SET_SIDEBAR_STATUS } from 'reducers/reducer';
import CollapsibleNavItem from './components/CollapsibleNavItem';
import BaseNavItem from './components/BaseNavItem';

const Navigation = ({ routes }) => {
  const {
    feWorkOrdersModuleEnableModule,
    feWorkOrdersEnableModuleByOrg,
    feApplicationsModuleEnableModule,
    feApplicationsEnableModuleByOrg,
    feInventoryModuleEnableModule,
    beProductPriceListEnabled,
    beProductPriceListEnableByOrgId,
    feProductPriceList,
    feYieldV2ModuleEnableModule,
    beYieldEnableByOrgId,
    beJohnDeereEnabledForAll,
    beJohnDeereEnabledPerOrg,
    feYieldModuleEnableModule,
    feReportsModuleEnableModule,
    releaseFeLegacyTaskDecommission,
    releaseFeLegacyTaskDecommissionByOrg
  } = useFlags();
  const [leftNavFilterRoutes, setLeftNavFilterRoutes] = useState(routes);
  const [
    { organization, loggedInUserOrgPermission, loadingOrg },
    dispatch
  ] = useContext(Context);
  const [isOpened, setIsOpened] = useState(false);

  useEffect(() => {
    const routeFilter = (route, isSubRoute) => {
      if (route.nav !== 'leftNav' && !isSubRoute) {
        return false;
      }
      if (route.id === 'workOrders') {
        return featureFlagRenderItem(
          feWorkOrdersModuleEnableModule,
          feWorkOrdersEnableModuleByOrg,
          organization?.id
        );
      }
      if (route.id === 'tasks') {
        return !featureFlagRenderItem(
          releaseFeLegacyTaskDecommission,
          releaseFeLegacyTaskDecommissionByOrg,
          organization?.id
        );
      }
      if (route.id === 'applications') {
        return featureFlagRenderItem(
          feApplicationsModuleEnableModule,
          feApplicationsEnableModuleByOrg,
          organization?.id
        );
      }
      if (route.id === 'inventory') {
        return feInventoryModuleEnableModule;
      }
      if (route.id === 'products') {
        return (
          feProductPriceList &&
          featureFlagRenderItem(
            beProductPriceListEnabled,
            beProductPriceListEnableByOrgId,
            organization?.id
          )
        );
      }
      if (route.id === 'reports') {
        return feReportsModuleEnableModule;
      }
      if (route.id === 'yield' && route.key === 'yield-v1') {
        return feYieldModuleEnableModule;
      }
      if (route.id === 'yield' && route.key === 'yieldV2') {
        return featureFlagRenderItem(
          feYieldV2ModuleEnableModule,
          beYieldEnableByOrgId,
          organization?.id
        );
      }
      return (
        loggedInUserOrgPermission?.role !== 'View Only' ||
        route.id !== 'organization'
      );
    };

    const filteredRoutes = routes
      .map(route => {
        if (route.subRoutes) {
          return {
            ...route,
            subRoutes: route.subRoutes.filter(subRoute =>
              routeFilter(subRoute, true)
            )
          };
        }
        return route;
      })
      .filter(route => routeFilter(route));

    setLeftNavFilterRoutes(filteredRoutes);
  }, [
    organization,
    routes,
    loggedInUserOrgPermission,
    feInventoryModuleEnableModule,
    feApplicationsModuleEnableModule,
    feApplicationsEnableModuleByOrg,
    feWorkOrdersModuleEnableModule,
    feWorkOrdersEnableModuleByOrg,
    feProductPriceList,
    feReportsModuleEnableModule,
    feYieldModuleEnableModule,
    beYieldEnableByOrgId,
    feYieldV2ModuleEnableModule,
    beJohnDeereEnabledForAll,
    beJohnDeereEnabledPerOrg,
    beProductPriceListEnabled,
    beProductPriceListEnableByOrgId,
    releaseFeLegacyTaskDecommission,
    releaseFeLegacyTaskDecommissionByOrg
  ]);

  const getStatus = () => !loadingOrg;

  const handleSidebarToggle = toggle => {
    dispatch({
      type: SET_SIDEBAR_STATUS,
      payload: !toggle
    });
  };

  return (
    <div data-testid="nav-lg">
      <Navbar
        pathToBrandIcon={brandIcon}
        pathToBrandText={brandText}
        setIsOpened={state => setIsOpened(!state)}
        defaultOpened={false}
        onClick={handleSidebarToggle}
      >
        {getStatus() &&
          leftNavFilterRoutes.map(item =>
            item?.subRoutes ? (
              <CollapsibleNavItem
                key={item.key}
                item={item}
                isOpened={isOpened}
              />
            ) : (
              <BaseNavItem key={item.key} item={item} isOpened={isOpened} />
            )
          )}
      </Navbar>
    </div>
  );
};

Navigation.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default withLDConsumer()(Navigation);
